<template>
  <div class="pedidos-view">
    <!-- Cabecera -->
    <div class="pv-header">
      <div class="pv-header-left">
        <div class="breadcrumb-q">
          <span class="bc-item" @click="$router.push('/dashboard')">Inicio</span>
          <span class="bc-sep">›</span>
          <span class="bc-current">Pedidos</span>
        </div>
        <h2 class="pv-title">Pedidos</h2>
      </div>
    </div>

    <!-- Barra de filtros -->
    <div class="pv-filtros-card">
      <!-- Presets de fecha -->
      <div class="pv-periodo-wrap">
        <div class="periodo-btns">
          <button v-for="p in periodos" :key="p.value"
            :class="['btn-periodo', { active: periodoActivo === p.value }]"
            @click="aplicarPeriodo(p.value)">
            <i v-if="p.value === 'custom'" class="fas fa-calendar-alt me-1"></i>
            {{ p.label }}
          </button>
        </div>
        <div v-if="periodoActivo === 'custom'" class="custom-dates mt-2">
          <input type="date" v-model="filtros.desde" class="form-control date-input" :max="filtros.hasta || undefined" />
          <span class="sep-dash">—</span>
          <input type="date" v-model="filtros.hasta" class="form-control date-input" :min="filtros.desde || undefined" />
          <button class="btn-periodo active" :disabled="!filtros.desde || !filtros.hasta" @click="cargarDatos">Aplicar</button>
        </div>
      </div>

      <!-- Fila de filtros -->
      <div class="pv-filtros-row">
        <div class="pv-search-wrap">
          <i class="fa fa-search pv-search-icon"></i>
          <input v-model="filtroTexto" type="text" class="pv-search-input"
            placeholder="Referencia, email, Telegram, producto…"
            @input="resetPagina" />
        </div>

        <select v-if="miRol == 1" v-model="filtros.tipster_id" class="pv-select" @change="cargarDatos">
          <option value="">Todos los tipsters</option>
          <option v-for="t in listaTipsters" :key="t.id" :value="t.id">{{ t.nombre }}</option>
        </select>

        <select v-model="filtros.producto_id" class="pv-select" @change="cargarDatos">
          <option value="">Todos los productos</option>
          <option v-for="p in productosDisponibles" :key="p.id" :value="p.id">{{ p.nombre }}</option>
        </select>

        <div class="pv-leyenda-estados">
          <button class="pv-ley-item" :class="{ 'pv-ley-activo': filtros.estado === 'C' }" @click="setFiltroEstado('C')">
            <span class="badge-estado badge-confirmado">Confirmado</span>
          </button>
          <button class="pv-ley-item" :class="{ 'pv-ley-activo': filtros.estado === 'P' }" @click="setFiltroEstado('P')">
            <span class="badge-estado badge-pendiente">Pendiente</span>
          </button>
          <button class="pv-ley-item" :class="{ 'pv-ley-activo': filtros.estado === 'C_dev' }" @click="setFiltroEstado('C_dev')">
            <span class="badge-estado badge-devuelto">Devuelto</span>
          </button>
          <button class="pv-ley-item" :class="{ 'pv-ley-activo': filtros.estado === 'F' }" @click="setFiltroEstado('F')">
            <span class="badge-estado badge-fallido">Fallido</span>
          </button>
          <button class="pv-ley-item" :class="{ 'pv-ley-activo': filtros.estado === '' }" @click="setFiltroEstado('')">
            <span class="badge-estado badge-todos">Todos</span>
          </button>
        </div>

        <select v-model="filtros.pais" class="pv-select" @change="cargarDatos">
          <option value="">Todos los países</option>
          <option v-for="code in paisesDisponibles" :key="code" :value="code">{{ code }}</option>
        </select>

        <select v-model="filtros.moneda" class="pv-select" @change="cargarDatos">
          <option value="">Todas las monedas</option>
          <option value="EUR">EUR €</option>
          <option value="USD">USD $</option>
        </select>

        <select v-model="filtros.tipo_pago" class="pv-select" @change="cargarDatos">
          <option value="">Suscripción + pago único</option>
          <option value="suscripcion">Solo suscripción</option>
          <option value="unico">Solo pago único</option>
        </select>

        <button class="pv-btn-reset" @click="resetFiltros" title="Limpiar filtros">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Tabla -->
    <div class="pv-table-card">
      <div class="pv-table-toolbar">
        <span class="pv-count">
          <template v-if="loading">Cargando…</template>
          <template v-else>{{ totales }} pedidos encontrados</template>
        </span>
        <div class="pv-toolbar-right">
          <button class="pv-col-btn" @click="selectorAbierto = !selectorAbierto" title="Columnas visibles">
            <i class="fas fa-columns"></i>
          </button>
        </div>

        <!-- Selector columnas -->
        <div v-if="selectorAbierto" class="pv-col-selector" v-click-outside="() => selectorAbierto = false">
          <div class="pv-col-header">Columnas visibles</div>
          <label v-for="col in columnasConfig" :key="col.key" class="pv-col-item" :class="{ fija: col.fija }">
            <input type="checkbox" v-model="colVis[col.key]" :disabled="col.fija" @change="guardarCols" />
            <span class="pv-col-dot" :style="{ background: col.color }"></span>
            {{ col.label }}
          </label>
        </div>
      </div>

      <div v-if="loading" class="pv-loading">
        <i class="fa fa-spinner fa-spin"></i> Cargando pedidos…
      </div>
      <div v-else-if="!filas.length" class="pv-empty">
        No se encontraron pedidos con los filtros aplicados.
      </div>

      <div v-else class="pv-scroll-wrap">
        <table class="pv-table">
          <thead>
            <tr>
              <th class="th-ver"></th>
              <th @click="ordenar('fecha')" class="th-s">
                Fecha <span class="ord">{{ iconOrden('fecha') }}</span>
              </th>
              <th v-if="colVis.referencia" @click="ordenar('referencia')" class="th-s">
                Referencia <span class="ord">{{ iconOrden('referencia') }}</span>
              </th>
              <th v-if="colVis.email" class="th-ns">Email / Telegram</th>
              <th @click="ordenar('producto')" class="th-s">
                Producto <span class="ord">{{ iconOrden('producto') }}</span>
              </th>
              <th v-if="miRol == 1 && colVis.tipster" @click="ordenar('tipster')" class="th-s">
                Tipster <span class="ord">{{ iconOrden('tipster') }}</span>
              </th>
              <th v-if="colVis.precio" @click="ordenar('precio')" class="th-s th-r">
                Precio <span class="ord">{{ iconOrden('precio') }}</span>
              </th>
              <th v-if="colVis.pagado" class="th-ns th-r">Últ. cobro</th>
              <th @click="ordenar('estado')" class="th-s">
                Estado <span class="ord">{{ iconOrden('estado') }}</span>
              </th>
              <th v-if="colVis.susc_estado" class="th-ns">Suscripción</th>
              <th v-if="colVis.pais" class="th-ns">País</th>
              <th v-if="colVis.moneda" class="th-ns">Moneda</th>
              <th v-if="colVis.tipo_compra" class="th-ns">Tipo</th>
              <th v-if="colVis.campana" class="th-ns">Campaña</th>
              <th v-if="colVis.canal" class="th-ns">Canal</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in filasPaginadas" :key="p.id" class="pv-row">
              <td class="td-ver">
                <button class="btn-ver" @click="abrirDetalle(p)" title="Ver detalle">
                  <i class="fas fa-eye"></i>
                </button>
              </td>
              <td class="td-fecha">{{ fmtFechaSolo(p.fecha) }}</td>
              <td v-if="colVis.referencia" class="td-ref">{{ p.referencia }}</td>
              <td v-if="colVis.email" class="td-email">
                <div v-if="p.cliente_email" class="td-email-main">{{ p.cliente_email }}</div>
                <div v-if="p.telegram_username || p.telegram_nombre || p.telegram_id" class="td-tg">
                  <i class="fa fa-telegram" style="color:#26a5e4;font-size:10px;margin-right:3px"></i>
                  {{ p.telegram_username ? '@' + p.telegram_username : (p.telegram_nombre || p.telegram_id) }}
                </div>
              </td>
              <td class="td-prod">{{ p.producto }}</td>
              <td v-if="miRol == 1 && colVis.tipster" class="td-tipster">{{ p.tipster }}</td>
              <td v-if="colVis.precio" class="td-precio">
                <div class="precio-lista">{{ fmtMoney(p.precio_pagado || p.precio, p.moneda) }}</div>
                <div v-if="p.precio_pagado && p.precio_pagado !== p.precio" class="precio-original">
                  {{ fmtMoney(p.precio, p.moneda) }}
                </div>
              </td>
              <td v-if="colVis.pagado" class="td-precio">
                <template v-if="p.es_trial">
                  <span class="badge-trial-sm">Trial</span>
                </template>
                <template v-else-if="p.precio_pagado > 0">
                  {{ fmtMoney(p.precio_pagado, p.moneda) }}
                </template>
                <template v-else>—</template>
              </td>
              <td>
                <span class="badge-estado" :class="badgeEstado(p)">{{ labelEstado(p) }}</span>
              </td>
              <td v-if="colVis.susc_estado" class="td-susc-col">
                <div v-if="p.es_suscripcion && p.susc_estado" class="pv-susc-cel">
                  <span :class="['badge-susc', 'badge-susc-' + p.susc_estado]">{{ p.susc_estado }}</span>
                  <span v-if="p.susc_cancel_prog" class="pv-susc-cancel-prog">En proceso de cancelación</span>
                </div>
                <span v-else-if="p.es_suscripcion" class="td-muted">susc.</span>
                <span v-else class="td-muted">—</span>
              </td>
              <td v-if="colVis.pais" class="td-pais">
                <span v-if="p.pais_compra"
                  :class="'fi fi-' + p.pais_compra.toLowerCase() + ' fis flag-icon me-1'"></span>
                {{ p.pais_compra || '—' }}
              </td>
              <td v-if="colVis.moneda">
                <span :class="['badge-moneda', p.moneda === 'USD' ? 'badge-usd' : 'badge-eur']">{{ p.moneda }}</span>
              </td>
              <td v-if="colVis.tipo_compra" class="td-muted">{{ labelTipoCompra(p.tipo_compra) }}</td>
              <td v-if="colVis.campana" class="td-campana">{{ p.campana_nombre || '—' }}</td>
              <td v-if="colVis.canal" class="td-muted">{{ p.canal_nombre || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="totalPaginas > 1" class="pv-pagination">
        <button class="pv-pg-btn" :disabled="paginaActual <= 1" @click="irPagina(1)">«</button>
        <button class="pv-pg-btn" :disabled="paginaActual <= 1" @click="irPagina(paginaActual - 1)">‹</button>
        <button v-for="n in paginasVisibles" :key="n"
          class="pv-pg-btn pv-pg-num" :class="{ activo: n === paginaActual }"
          @click="irPagina(n)">{{ n }}</button>
        <button class="pv-pg-btn" :disabled="paginaActual >= totalPaginas" @click="irPagina(paginaActual + 1)">›</button>
        <button class="pv-pg-btn" :disabled="paginaActual >= totalPaginas" @click="irPagina(totalPaginas)">»</button>
        <span class="pv-pg-info">{{ paginaActual }} / {{ totalPaginas }}</span>
      </div>
    </div>

    <!-- Modal detalle -->
    <div v-if="modalDetalle" class="pv-modal-overlay" @click.self="cerrarDetalle">
      <div class="pv-modal-box">
        <div class="pv-modal-header">
          <div>
            <div class="pv-modal-titulo">Pedido {{ modalPedido?.referencia }}</div>
            <div class="pv-modal-sub">{{ fmtFecha(modalPedido?.fecha) }}</div>
          </div>
          <button class="pv-modal-close" @click="cerrarDetalle">✕</button>
        </div>

        <div class="pv-modal-body">
          <div class="det-grid">
            <div class="det-item">
              <div class="det-label">Producto</div>
              <div class="det-value">{{ modalPedido?.producto }}</div>
            </div>
            <div class="det-item" v-if="miRol == 1">
              <div class="det-label">Tipster</div>
              <div class="det-value">{{ modalPedido?.tipster }}</div>
            </div>
            <div class="det-item">
              <div class="det-label">Precio</div>
              <div class="det-value">{{ fmtMoney(modalPedido?.precio, modalPedido?.moneda) }}</div>
            </div>
            <div class="det-item">
              <div class="det-label">Estado pedido</div>
              <div class="det-value">
                <span class="badge-estado" :class="badgeEstado(modalPedido)">{{ labelEstado(modalPedido) }}</span>
              </div>
            </div>
            <div class="det-item" v-if="modalPedido?.cliente_email">
              <div class="det-label">Email</div>
              <div class="det-value">{{ modalPedido.cliente_email }}</div>
            </div>
            <div class="det-item" v-if="modalPedido?.telegram_id">
              <div class="det-label">Telegram ID</div>
              <div class="det-value">{{ modalPedido.telegram_id }}</div>
            </div>
            <div class="det-item" v-if="modalPedido?.pais_compra">
              <div class="det-label">País</div>
              <div class="det-value">
                <span :class="'fi fi-' + modalPedido.pais_compra.toLowerCase() + ' fis flag-icon me-1'"></span>
                {{ modalPedido.pais_compra }}
              </div>
            </div>
            <div class="det-item" v-if="modalPedido?.moneda">
              <div class="det-label">Moneda</div>
              <div class="det-value">{{ modalPedido.moneda }}</div>
            </div>
            <div class="det-item" v-if="modalPedido?.tipo_compra">
              <div class="det-label">Tipo</div>
              <div class="det-value">{{ labelTipoCompra(modalPedido.tipo_compra) }}</div>
            </div>
            <div class="det-item" v-if="modalPedido?.metodo_pago">
              <div class="det-label">Método de pago</div>
              <div class="det-value">{{ modalPedido.metodo_pago }}</div>
            </div>
            <div class="det-item" v-if="modalPedido?.campana_nombre">
              <div class="det-label">Campaña</div>
              <div class="det-value">{{ modalPedido.campana_nombre }}</div>
            </div>
            <div class="det-item" v-if="modalPedido?.canal_nombre">
              <div class="det-label">Canal</div>
              <div class="det-value">{{ modalPedido.canal_nombre }}</div>
            </div>
          </div>

          <!-- Enlace de invitación -->
          <div class="det-section mt-3">
            <div class="det-section-title">Enlace de invitación al canal</div>

            <div v-if="!modalPedido?.invite_link && !enlaceAnterior" class="det-empty">
              Este pedido no tiene enlace de invitación generado.
            </div>

            <div v-else-if="!nuevoEnlace" class="det-invite">
              <div class="det-invite-row">
                <div class="det-label">Enlace actual</div>
                <a :href="modalPedido.invite_link" target="_blank" rel="noopener" class="det-link">
                  {{ modalPedido.invite_link }}
                </a>
              </div>
              <div class="det-invite-row" style="margin-top:6px">
                <div class="det-label">Estado</div>
                <div v-if="modalPedido.invite_used_at || modalPedido.telegram_id">
                  <span class="badge-usado">Usado</span>
                  <span class="det-usado-info">
                    <template v-if="modalPedido.invite_used_at">el {{ fmtFecha(modalPedido.invite_used_at) }}&nbsp;</template>
                    <template v-if="modalPedido.telegram_id">por Telegram ID <b>{{ modalPedido.telegram_id }}</b></template>
                  </span>
                </div>
                <div v-else>
                  <span class="badge-sinusar">Sin usar</span>
                  <span class="det-usado-info">El usuario aún no ha accedido al canal</span>
                </div>
              </div>
            </div>

            <div v-if="nuevoEnlace" class="det-invite" style="margin-top:8px">
              <div class="det-invite-row">
                <div class="det-label">Enlace anterior <span class="badge-revocado">Revocado</span></div>
                <span class="det-link det-link--revocado">{{ enlaceAnterior || '—' }}</span>
              </div>
              <div class="det-invite-row" style="margin-top:6px">
                <div class="det-label">Nuevo enlace <span class="badge-nuevo">Uso único</span></div>
                <a :href="nuevoEnlace" target="_blank" rel="noopener" class="det-link det-link--nuevo">{{ nuevoEnlace }}</a>
              </div>
            </div>

            <div v-if="regenMsg" class="det-regen-msg" :class="regenOk ? 'det-regen-ok' : 'det-regen-err'" style="margin-top:8px">
              {{ regenMsg }}
            </div>
          </div>

          <!-- Estado Stripe si pedido fallido -->
          <div v-if="modalPedido?.estado === 'F'" class="det-section mt-3">
            <div class="det-section-title">Motivo del fallo</div>
            <div v-if="stripeErrorLoading" class="det-loading">Consultando Stripe…</div>
            <div v-else-if="stripeErrorMsg" class="det-stripe-error">{{ stripeErrorMsg }}</div>
          </div>

          <!-- Historial inline -->
          <div class="det-section mt-3">
            <div class="det-section-title">Histórico de pagos</div>
            <div v-if="historialLoading" class="det-loading">Cargando historial…</div>
            <div v-else-if="!historialEventos.length" class="det-empty">Sin registros de pago para este pedido.</div>
            <div v-else class="hist-timeline">
              <div v-for="(ev, idx) in historialEventos" :key="idx"
                :class="['hist-evento', 'hist-' + ev.tipo]">
                <div class="hist-dot"></div>
                <div class="hist-contenido">
                  <div class="hist-label">{{ labelEvento(ev.tipo, idx) }}</div>
                  <div class="hist-fecha">{{ fmtFecha(ev.fecha) }}</div>
                  <div v-if="ev.importe !== null && ev.importe > 0" class="hist-importe">
                    {{ fmtMoney(ev.importe, ev.moneda) }}
                  </div>
                  <div v-else-if="ev.es_trial" class="hist-importe hist-importe--trial">Trial gratuito</div>
                  <div v-if="ev.motivo" class="hist-motivo">{{ ev.motivo }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pv-modal-foot">
          <button v-if="modalPedido?.invite_link || modalPedido?.canal_id"
            class="btn-regen" :disabled="regenerando" @click="regenerarEnlace">
            <span v-if="regenerando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="fas fa-rotate me-1"></i>
            {{ regenerando ? 'Regenerando…' : 'Regenerar enlace' }}
          </button>
          <button class="btn-cerrar-modal" @click="cerrarDetalle">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import panelesService from "@/services/paneles.service.js";

const POR_PAGINA = 25;

const COLS_DEFAULT = {
  referencia: true, email: true, tipster: true, precio: true,
  pagado: true, susc_estado: true, pais: true, moneda: true,
  tipo_compra: false, campana: false, canal: false,
};
const COLS_MOBILE = {
  referencia: false, email: true, tipster: false, precio: true,
  pagado: false, susc_estado: false, pais: false, moneda: false,
  tipo_compra: false, campana: false, canal: false,
};
const STORAGE_KEY = "pedidos_v2_cols";

export default {
  name: "Pedidos",

  data() {
    const hoy = new Date();
    const fmt = d => d.toISOString().slice(0, 10);
    const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return {
      filas:        [],
      totales:      0,
      loading:      false,
      paginaActual: 1,
      ordenCol:     "fecha",
      ordenDir:     "desc",
      periodoActivo: "mes",
      filtroTexto:  "",
      selectorAbierto: false,
      colVis: { ...COLS_DEFAULT },
      periodos: [
        { value: 'mes',      label: 'Mes actual' },
        { value: 'hoy',      label: 'Hoy' },
        { value: 'hoy_ayer', label: 'Hoy y Ayer' },
        { value: 'anio',     label: 'Este año' },
        { value: 'all',      label: 'Todo' },
        { value: 'custom',   label: 'Personalizado' },
      ],

      filtros: {
        desde:       fmt(inicioMes),
        hasta:       fmt(hoy),
        tipster_id:  "",
        producto_id: "",
        estado:      "C",
        pais:        "",
        moneda:      "",
        tipo_pago:   "",
      },

      listaTipsters: [],

      // modal
      modalDetalle:       false,
      modalPedido:        null,
      historialLoading:   false,
      historialEventos:   [],
      stripeErrorLoading: false,
      stripeErrorMsg:     null,

      // regenerar enlace
      regenerando:    false,
      regenMsg:       "",
      regenOk:        false,
      nuevoEnlace:    "",
      enlaceAnterior: "",
    };
  },

  computed: {
    miRol() {
      const fromStore = this.$store.getters["auth/rolEfectivo"];
      if (fromStore) return fromStore;
      return Number(localStorage.getItem("usuarioRolId") || 0);
    },

    columnasConfig() {
      const cols = [
        { key: "referencia",  label: "Referencia",   color: "#6b7280", fija: false },
        { key: "email",       label: "Email/Telegram", color: "#6b7280", fija: false },
        { key: "tipster",     label: "Tipster",       color: "#6b7280", fija: false },
        { key: "precio",      label: "Precio",        color: "#d4af37", fija: false },
        { key: "pagado",      label: "Últ. cobro",    color: "#66bb6a", fija: false },
        { key: "susc_estado", label: "Suscripción",   color: "#42a5f5", fija: false },
        { key: "pais",        label: "País",          color: "#6b7280", fija: false },
        { key: "moneda",      label: "Moneda",        color: "#26c6da", fija: false },
        { key: "tipo_compra", label: "Tipo compra",   color: "#9c27b0", fija: false },
        { key: "campana",     label: "Campaña",       color: "#ff9800", fija: false },
        { key: "canal",       label: "Canal",         color: "#4caf50", fija: false },
      ];
      return this.miRol == 1 ? cols : cols.filter(c => c.key !== "tipster");
    },

    filasOrdenadas() {
      const txt = (this.filtroTexto || "").toLowerCase().trim();
      let lista = txt
        ? this.filas.filter(p =>
            (p.referencia     || "").toLowerCase().includes(txt) ||
            (p.cliente_email  || "").toLowerCase().includes(txt) ||
            (p.producto       || "").toLowerCase().includes(txt) ||
            (p.telegram_id    || "").toString().includes(txt) ||
            (p.telegram_username || "").toLowerCase().includes(txt) ||
            (p.campana_nombre || "").toLowerCase().includes(txt)
          )
        : [...this.filas];

      lista.sort((a, b) => {
        let va = a[this.ordenCol] ?? "";
        let vb = b[this.ordenCol] ?? "";
        if (["precio", "precio_pagado"].includes(this.ordenCol)) { va = Number(va); vb = Number(vb); }
        if (va < vb) return this.ordenDir === "asc" ? -1 : 1;
        if (va > vb) return this.ordenDir === "asc" ?  1 : -1;
        return 0;
      });

      return lista;
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.filasOrdenadas.length / POR_PAGINA));
    },

    filasPaginadas() {
      const ini = (this.paginaActual - 1) * POR_PAGINA;
      return this.filasOrdenadas.slice(ini, ini + POR_PAGINA);
    },

    paisesDisponibles() {
      const set = new Set(this.filas.map(p => p.pais_compra).filter(Boolean));
      return [...set].sort();
    },

    productosDisponibles() {
      const seen = new Map();
      this.filas.forEach(p => { if (p.producto_id && p.producto) seen.set(p.producto_id, p.producto); });
      return [...seen.entries()].map(([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre));
    },

    paginasVisibles() {
      const total = this.totalPaginas;
      const cur   = this.paginaActual;
      const delta = 2;
      const pages = [];
      for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {
        pages.push(i);
      }
      return pages;
    },
  },

  watch: {
    filtroTexto() {
      this.paginaActual = 1;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => { this.cargarDatos(); }, 400);
    },
  },

  async created() {
    this.cargarCols();
    const esMobile = window.innerWidth < 768;
    if (esMobile) this.colVis = { ...COLS_MOBILE };
    await this.cargarExtras();
    await this.cargarDatos();
  },

  methods: {
    // ── Carga de datos ──────────────────────────────────────────────────────
    async cargarDatos() {
      this.loading = true;
      try {
        const payload = {
          paginaActual:    1,
          sortColumn:      0,
          sortDirection:   "desc",
          mostrarBorrados: false,
          textoBuscador: this.filtroTexto       || null,
          desde:         this.filtros.desde     || null,
          hasta:         this.filtros.hasta     || null,
          tipster_id:    this.filtros.tipster_id  || null,
          producto_id:   this.filtros.producto_id || null,
          estado:        this.filtros.estado      || null,
          pais:          this.filtros.pais         || null,
          moneda:        this.filtros.moneda       || null,
          tipo_pago:     this.filtros.tipo_pago    || null,
        };
        const res = await this.$store.dispatch("auth/pedidos", payload);
        this.filas   = Array.isArray(res?.data) ? res.data : [];
        this.totales = res?.totales ?? this.filas.length;
        this.paginaActual = 1;
      } catch {
        this.filas   = [];
        this.totales = 0;
      } finally {
        this.loading = false;
      }
    },

    async cargarExtras() {
      try {
        const data = await this.$store.dispatch("auth/listaTipsters");
        this.listaTipsters = Array.isArray(data) ? data : [];
      } catch (e) { /* tipsters no disponibles para este rol */ }
    },

    // ── Presets de fecha ────────────────────────────────────────────────────
    aplicarPeriodo(value) {
      this.periodoActivo = value;
      if (value === 'custom') return; // espera inputs manuales + Aplicar
      const hoy = new Date();
      const f   = d => d.toISOString().slice(0, 10);
      if (value === 'all') {
        this.filtros.desde = "";
        this.filtros.hasta = "";
      } else if (value === 'mes') {
        this.filtros.desde = f(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
        this.filtros.hasta = f(hoy);
      } else if (value === 'hoy') {
        this.filtros.desde = f(hoy);
        this.filtros.hasta = f(hoy);
      } else if (value === 'hoy_ayer') {
        const ayer = new Date(hoy); ayer.setDate(ayer.getDate() - 1);
        this.filtros.desde = f(ayer);
        this.filtros.hasta = f(hoy);
      } else if (value === 'anio') {
        this.filtros.desde = f(new Date(hoy.getFullYear(), 0, 1));
        this.filtros.hasta = f(hoy);
      }
      this.cargarDatos();
    },

    setFiltroEstado(v) {
      this.filtros.estado = v;
      this.paginaActual = 1;
      this.cargarDatos();
    },

    resetFiltros() {
      this.filtros.tipster_id  = "";
      this.filtros.producto_id = "";
      this.filtros.estado      = "C";
      this.filtros.pais        = "";
      this.filtros.moneda      = "";
      this.filtros.tipo_pago   = "";
      this.filtroTexto = "";
      this.aplicarPeriodo('mes');
    },

    resetPagina() { this.paginaActual = 1; },

    // ── Ordenación ──────────────────────────────────────────────────────────
    ordenar(col) {
      if (this.ordenCol === col) {
        this.ordenDir = this.ordenDir === "asc" ? "desc" : "asc";
      } else {
        this.ordenCol = col;
        this.ordenDir = ["fecha", "precio"].includes(col) ? "desc" : "asc";
      }
      this.paginaActual = 1;
    },

    iconOrden(col) {
      if (this.ordenCol !== col) return "↕";
      return this.ordenDir === "asc" ? "↑" : "↓";
    },

    // ── Paginación ──────────────────────────────────────────────────────────
    irPagina(n) {
      this.paginaActual = Math.max(1, Math.min(n, this.totalPaginas));
    },

    // ── Modal ───────────────────────────────────────────────────────────────
    abrirDetalle(p) {
      this.modalPedido        = { ...p };
      this.historialEventos   = [];
      this.stripeErrorMsg     = null;
      this.stripeErrorLoading = false;
      this.regenerando        = false;
      this.regenMsg           = "";
      this.regenOk            = false;
      this.nuevoEnlace        = "";
      this.enlaceAnterior     = "";
      this.modalDetalle       = true;
      if (p.estado === "F") this.fetchStripeError(p);
      this.cargarHistorial(p.id);
    },

    cerrarDetalle() {
      this.modalDetalle = false;
      this.modalPedido  = null;
    },

    async cargarHistorial(pedidoId) {
      this.historialLoading = true;
      this.historialEventos = [];
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/pedido/${pedidoId}/historial`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.historialEventos = res.data?.eventos || [];
      } catch {
        this.historialEventos = [];
      } finally {
        this.historialLoading = false;
      }
    },

    async regenerarEnlace() {
      if (!this.modalPedido) return;
      this.regenerando    = true;
      this.regenMsg       = "";
      this.regenOk        = false;
      this.nuevoEnlace    = "";
      this.enlaceAnterior = "";
      try {
        const tipsterId = this.modalPedido.tipster_id || null;
        const data = await panelesService.regenerarEnlacePedido(this.modalPedido.id, tipsterId);
        if (data?.res === "OK") {
          this.regenOk        = true;
          this.regenMsg       = data.mensaje || "Enlace regenerado correctamente";
          this.enlaceAnterior = this.modalPedido.invite_link || "";
          this.nuevoEnlace    = data.invite_link || "";
          this.modalPedido.invite_link    = data.invite_link || null;
          this.modalPedido.invite_used_at = null;
          if (data.kick_ok) this.modalPedido.telegram_id = null;
        } else {
          this.regenOk  = false;
          this.regenMsg = data?.mensaje || "No se pudo regenerar el enlace";
        }
      } catch (e) {
        this.regenOk  = false;
        this.regenMsg = e?.response?.data?.mensaje || "Error al regenerar el enlace";
      } finally {
        this.regenerando = false;
      }
    },

    async fetchStripeError(p) {
      if (!p?.id) return;
      if (!p.paymentId) {
        this.stripeErrorMsg = "Pago abandonado — el cliente no completó el proceso de pago.";
        return;
      }
      this.stripeErrorLoading = true;
      try {
        const token    = JSON.parse(localStorage.getItem("userF"));
        const tipsterId = p.tipster_id || "";
        if (!tipsterId) return;
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${tipsterId}/stripe-error/${p.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.stripeErrorMsg = res.data?.motivo || res.data?.mensaje || "Sin información disponible.";
      } catch (e) {
        this.stripeErrorMsg = e?.response?.data?.mensaje || "Error al consultar Stripe.";
      } finally {
        this.stripeErrorLoading = false;
      }
    },

    // ── Columnas ────────────────────────────────────────────────────────────
    guardarCols() {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.colVis)); } catch (e) { /* storage bloqueado */ }
    },

    cargarCols() {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        if (saved) this.colVis = { ...COLS_DEFAULT, ...saved };
      } catch (e) { /* storage bloqueado */ }
    },

    // ── Formato ─────────────────────────────────────────────────────────────
    fmtFecha(f) {
      if (!f) return "—";
      const m = String(f).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
      if (!m) return f;
      return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
    },

    fmtFechaSolo(f) {
      if (!f) return "—";
      const parte = typeof f === "string" ? f.slice(0, 10) : new Date(f).toISOString().slice(0, 10);
      const [y, mo, d] = parte.split("-");
      return `${d}/${mo}/${y}`;
    },

    fmtMoney(n, moneda) {
      const cur = (moneda || "EUR").toUpperCase();
      return Number(n || 0).toLocaleString(cur === "USD" ? "en-US" : "es-ES", { style: "currency", currency: cur });
    },

    labelEstado(p) {
      if (!p) return "—";
      if (p.devuelto || p.es_devuelto) return "Devuelto";
      if (p.es_trial)    return "Trial activo";
      if (p.estado === "C") return "Confirmado";
      if (p.estado === "P") return "Pendiente";
      if (p.estado === "F") return "Fallido";
      return p.estado || "—";
    },

    badgeEstado(p) {
      if (!p) return "badge-neutro";
      if (p.devuelto || p.es_devuelto) return "badge-devuelto";
      if (p.es_trial)    return "badge-trial";
      if (p.estado === "C") return "badge-confirmado";
      if (p.estado === "P") return "badge-pendiente";
      if (p.estado === "F") return "badge-fallido";
      return "badge-neutro";
    },

    labelTipoCompra(t) {
      return { compra_directa: "Directa", suscripcion: "Suscripción" }[t] ?? (t || "—");
    },

    labelEvento(tipo, idx) {
      if (tipo === "pago_ok" || tipo === "pago_recuperado") {
        const prev = this.historialEventos
          .slice(0, idx)
          .filter(e => e.tipo === "pago_ok" || e.tipo === "pago_recuperado").length;
        if (tipo === "pago_recuperado") return "Pago recuperado";
        return prev === 0 ? "Primer pago" : "Renovación";
      }
      return { creada: "Alta", pago_fallido: "Pago fallido", cancelada: "Cancelada", vencida: "Vencida" }[tipo] || tipo;
    },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────────── */
.pedidos-view {
  padding: 20px 20px 60px;
  min-height: 100vh;
  background: var(--q-bg, #0e1117);
  color: var(--q-text, #e8eaf0);
  font-family: inherit;
}

.pv-header { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
.pv-header-left { flex: 1; }
.breadcrumb-q { font-size: 12px; opacity: .55; margin-bottom: 4px; }
.bc-item { cursor: pointer; }
.bc-item:hover { opacity: 1; color: var(--q-gold, #d4af37); }
.bc-sep { margin: 0 5px; }
.pv-title { font-size: 20px; font-weight: 700; color: var(--q-gold, #d4af37); margin: 0; }

/* ── Filtros ─────────────────────────────────────────────────────────────── */
.pv-filtros-card {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.pv-periodo-wrap { margin-bottom: 10px; }
.periodo-btns { display: flex; gap: 6px; flex-wrap: wrap; }
.btn-periodo {
  padding: 5px 14px;
  border-radius: 8px;
  border: 1px solid var(--q-border-md);
  background: transparent;
  color: var(--q-muted);
  font-size: .8rem;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
  white-space: nowrap;
}
.btn-periodo:hover:not(:disabled) { border-color: var(--q-gold); color: var(--q-text); }
.btn-periodo.active {
  background: var(--q-gold);
  border-color: var(--q-gold);
  color: #140c00;
  font-weight: 600;
}
.btn-periodo:disabled { opacity: .5; cursor: not-allowed; }
.custom-dates {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.date-input {
  max-width: 150px; font-size: .85rem;
  background: var(--q-card); color: var(--q-text);
  border-color: var(--q-border-md);
}
.date-input:focus { border-color: var(--q-gold); box-shadow: none; }
.sep-dash { color: var(--q-muted); font-size: .9rem; }

.pv-filtros-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.pv-search-wrap {
  position: relative; flex: 1; min-width: 200px;
}
.pv-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); opacity: .4; font-size: 12px; }
.pv-search-input {
  width: 100%; padding: 7px 10px 7px 28px;
  border-radius: 8px; border: 1px solid var(--q-border, #2a2f3d);
  background: var(--q-card-alt, #1e2535);
  color: var(--q-text, #e8eaf0); font-size: 13px;
}
.pv-search-input:focus { outline: none; border-color: var(--q-gold, #d4af37); }

.pv-select {
  padding: 7px 10px; border-radius: 8px;
  border: 1px solid var(--q-border, #2a2f3d);
  background: var(--q-card-alt, #1e2535);
  color: var(--q-text, #e8eaf0); font-size: 12px; cursor: pointer;
}
.pv-btn-reset {
  padding: 7px 12px; border-radius: 8px; border: 1px solid var(--q-border, #2a2f3d);
  background: transparent; color: var(--q-text, #e8eaf0); font-size: 13px; cursor: pointer;
  opacity: .6;
}
.pv-btn-reset:hover { opacity: 1; color: #ef5350; border-color: #ef5350; }

/* ── Tabla ───────────────────────────────────────────────────────────────── */
.pv-table-card {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 12px;
  overflow: hidden;
}

.pv-table-toolbar {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; border-bottom: 1px solid var(--q-border, #2a2f3d);
  position: relative;
}
.pv-count { font-size: 13px; flex: 1; opacity: .7; }
.pv-toolbar-right { display: flex; gap: 6px; }
.pv-col-btn {
  padding: 5px 10px; border-radius: 8px; border: 1px solid var(--q-border, #2a2f3d);
  background: transparent; color: var(--q-text, #e8eaf0); font-size: 12px; cursor: pointer;
}
.pv-col-btn:hover { border-color: var(--q-gold, #d4af37); }

.pv-col-selector {
  position: absolute; top: calc(100% + 4px); right: 12px;
  background: var(--q-card-alt, #1e2535);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 10px; padding: 10px 12px; z-index: 100;
  min-width: 200px;
}
.pv-col-header { font-size: 11px; font-weight: 700; text-transform: uppercase; opacity: .5; margin-bottom: 8px; }
.pv-col-item {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; padding: 4px 0; cursor: pointer; white-space: nowrap;
}
.pv-col-item.fija { opacity: .4; cursor: default; }
.pv-col-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

.pv-loading, .pv-empty {
  padding: 50px 20px; text-align: center; opacity: .5; font-size: 14px;
}

.pv-scroll-wrap { overflow-x: auto; }

.pv-table {
  width: 100%; border-collapse: collapse;
  font-size: 0.82rem; min-width: 700px;
}

.pv-table thead th {
  background: var(--q-card-alt, #1e2535);
  border-bottom: 1px solid var(--q-border, #2a2f3d);
  padding: 8px 10px; text-align: left;
  font-weight: 600; font-size: 11px; letter-spacing: .04em;
  text-transform: uppercase; color: var(--q-muted, #8891aa);
  white-space: nowrap;
}
.th-s  { cursor: pointer; user-select: none; }
.th-ns { cursor: default; }
.th-r  { text-align: right; }
.ord   { opacity: .5; font-size: 10px; margin-left: 3px; }

.pv-table tbody td {
  padding: 9px 10px;
  border-bottom: 1px solid rgba(255,255,255,.04);
  vertical-align: middle;
}
.pv-table tbody tr:last-child td { border-bottom: none; }
.pv-row { transition: background .1s; }
.pv-row:hover td { background: rgba(255,255,255,.03); }

.th-ver { width: 38px; padding: 0 !important; }
.td-ver  { width: 38px; padding: 4px 6px !important; }
.btn-ver {
  width: 28px; height: 28px;
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 7px;
  background: var(--q-card-alt, #1e2535);
  color: var(--q-muted, #8891aa);
  font-size: 0.78rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: .15s;
}
.btn-ver:hover { border-color: var(--q-gold, #d4af37); color: var(--q-gold, #d4af37); }

.td-fecha    { white-space: nowrap; font-size: 12px; opacity: .8; }
.td-ref      { font-family: monospace; font-size: 12px; color: #a0aab8; }
.td-email    { max-width: 200px; }
.td-email-main { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-tg       { font-size: 11px; opacity: .6; margin-top: 2px; }
.td-prod     { max-width: 180px; font-size: 12px; font-weight: 600; }
.td-tipster  { font-size: 12px; opacity: .8; }
.td-precio   { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.td-pais     { white-space: nowrap; font-size: 12px; }
.td-campana  { max-width: 160px; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-muted    { font-size: 12px; opacity: .6; }
.precio-lista    { font-weight: 700; color: var(--q-gold, #d4af37); }
.precio-original { font-size: 11px; opacity: .5; text-decoration: line-through; }

/* Badges */
.badge-estado {
  display: inline-block; padding: 2px 8px; border-radius: 20px;
  font-size: 10px; font-weight: 700; white-space: nowrap;
}
.badge-confirmado { background: rgba(102,187,106,.18); color: #66bb6a; }
.badge-pendiente  { background: rgba(255,193,7,.18);   color: #ffc107; }
.badge-fallido    { background: rgba(239,83,80,.18);   color: #ef5350; }
.badge-devuelto   { background: rgba(100,116,139,.2);  color: #94a3b8; }
.badge-trial      { background: rgba(66,165,245,.18);  color: #42a5f5; }
.badge-neutro     { background: rgba(128,128,128,.15); color: #9ca3af; }

.badge-susc {
  display: inline-block; padding: 1px 7px; border-radius: 12px;
  font-size: 10px; font-weight: 600; white-space: nowrap;
}
.badge-susc-active   { background: rgba(102,187,106,.15); color: #66bb6a; }
.badge-susc-trialing { background: rgba(66,165,245,.15);  color: #42a5f5; }
.badge-susc-past_due { background: rgba(255,193,7,.15);   color: #ffc107; }
.badge-susc-canceled { background: rgba(100,116,139,.15); color: #94a3b8; }
.badge-susc-vencida  { background: rgba(100,116,139,.15); color: #94a3b8; }
.badge-susc-cancelada{ background: rgba(100,116,139,.15); color: #94a3b8; }

.badge-todos     { background: var(--q-hover, rgba(255,255,255,.06)); color: var(--q-muted, #9ca3af); }

.td-susc-col { text-align: center; }
.pv-susc-cel {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}
.pv-susc-cancel-prog {
  font-size: 0.67rem;
  color: #f87171;
  white-space: nowrap;
}

/* Botones filtro estado */
.pv-leyenda-estados {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.pv-ley-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--q-muted);
  white-space: nowrap;
  background: transparent;
  border: 1.5px solid var(--q-border);
  border-radius: 20px;
  padding: 3px 10px;
  cursor: pointer;
  transition: border-color .15s, background .15s;
}
.pv-ley-item:hover { border-color: var(--q-border-md); }
.pv-ley-item.pv-ley-activo {
  border-color: var(--q-border-md);
  background: var(--q-hover);
}
.pv-ley-item.pv-ley-activo:has(.badge-confirmado) { border-color: var(--q-green, #66bb6a); }
.pv-ley-item.pv-ley-activo:has(.badge-pendiente)  { border-color: var(--q-amber, #ffc107); }
.pv-ley-item.pv-ley-activo:has(.badge-devuelto)   { border-color: #94a3b8; }
.pv-ley-item.pv-ley-activo:has(.badge-fallido)    { border-color: #ef5350; }
.pv-ley-item.pv-ley-activo:has(.badge-todos)      { border-color: var(--q-border-md); }

.badge-moneda    { font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 10px; }
.badge-eur       { background: rgba(212,175,55,.15); color: #d4af37; }
.badge-usd       { background: rgba(38,198,218,.15); color: #26c6da; }

.badge-trial-sm  { font-size: 10px; padding: 1px 7px; border-radius: 10px; background: rgba(66,165,245,.15); color: #42a5f5; }

/* Paginación */
.pv-pagination {
  display: flex; align-items: center; gap: 4px; justify-content: center;
  padding: 12px 16px; border-top: 1px solid var(--q-border, #2a2f3d);
  flex-wrap: nowrap;
}
@media (max-width: 600px) {
  .pv-pg-num { display: none; }
  .pv-pagination { gap: 6px; }
}
.pv-pg-btn {
  min-width: 30px; height: 30px; padding: 0 8px; border-radius: 8px;
  border: 1px solid var(--q-border, #2a2f3d);
  background: transparent; color: var(--q-text, #e8eaf0);
  font-size: 12px; cursor: pointer; transition: all .15s;
}
.pv-pg-btn:hover:not(:disabled) { border-color: var(--q-gold, #d4af37); color: var(--q-gold, #d4af37); }
.pv-pg-btn.activo { background: rgba(212,175,55,.2); border-color: var(--q-gold, #d4af37); color: var(--q-gold, #d4af37); }
.pv-pg-btn:disabled { opacity: .3; cursor: default; }
.pv-pg-info { font-size: 12px; opacity: .5; margin-left: 8px; }

/* ── Modal ───────────────────────────────────────────────────────────────── */
.pv-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.6);
  z-index: 1050; display: flex; align-items: center;
  justify-content: center; padding: 16px;
}
.pv-modal-box {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 16px; width: 100%; max-width: 640px;
  max-height: 88vh; display: flex; flex-direction: column; overflow: hidden;
}
.pv-modal-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--q-border, #2a2f3d); flex-shrink: 0;
}
.pv-modal-titulo { font-size: 15px; font-weight: 700; color: var(--q-gold, #d4af37); }
.pv-modal-sub    { font-size: 12px; opacity: .55; margin-top: 2px; }
.pv-modal-close  { background: none; border: none; cursor: pointer; font-size: 16px; opacity: .5; color: var(--q-text, #e8eaf0); padding: 4px; }
.pv-modal-close:hover { opacity: 1; }

.pv-modal-body { overflow-y: auto; flex: 1; padding: 16px 20px; }

.det-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px 16px;
  margin-bottom: 8px;
}
.det-item { display: flex; flex-direction: column; gap: 3px; }
.det-label { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; opacity: .45; font-weight: 600; }
.det-value { font-size: 13px; font-weight: 600; }

.det-section { border-top: 1px solid var(--q-border, #2a2f3d); padding-top: 12px; }
.det-section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; opacity: .5; margin-bottom: 8px; }
.det-loading, .det-empty { font-size: 13px; opacity: .5; padding: 8px 0; }
.det-stripe-error { font-size: 13px; color: #ef9a9a; background: rgba(239,83,80,.08); padding: 10px 12px; border-radius: 8px; }

/* Timeline historial */
.hist-timeline { display: flex; flex-direction: column; gap: 0; position: relative; padding-left: 16px; }
.hist-timeline::before {
  content: ""; position: absolute; left: 5px; top: 8px; bottom: 8px;
  width: 2px; background: var(--q-border, #2a2f3d);
}
.hist-evento { display: flex; align-items: flex-start; gap: 10px; padding: 6px 0; position: relative; }
.hist-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; margin-top: 3px;
  background: var(--q-border, #2a2f3d); position: absolute; left: -16px;
  border: 2px solid var(--q-card, #161b25);
}
.hist-pago_ok .hist-dot, .hist-pago_recuperado .hist-dot { background: #66bb6a; }
.hist-pago_fallido .hist-dot { background: #ef5350; }
.hist-cancelada .hist-dot, .hist-vencida .hist-dot { background: #94a3b8; }
.hist-creada .hist-dot { background: #42a5f5; }

.hist-contenido { display: flex; flex-wrap: wrap; gap: 4px 12px; align-items: baseline; }
.hist-label  { font-size: 13px; font-weight: 600; }
.hist-fecha  { font-size: 11px; opacity: .5; }
.hist-importe { font-size: 13px; font-weight: 700; color: #66bb6a; font-variant-numeric: tabular-nums; }
.hist-importe--trial { color: #42a5f5; }
.hist-motivo { width: 100%; font-size: 12px; color: #ef9a9a; opacity: .85; }

/* ── Modal footer ───────────────────────────────────────────────────────── */
.pv-modal-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 12px 20px; border-top: 1px solid var(--q-border, #2a2f3d); flex-shrink: 0;
}
.btn-regen {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px;
  border: 1px solid var(--q-gold, #d4af37);
  background: rgba(212,175,55,.12);
  color: var(--q-gold, #d4af37); font-size: 13px; font-weight: 600; cursor: pointer; transition: .15s;
}
.btn-regen:hover:not(:disabled) { background: rgba(212,175,55,.22); }
.btn-regen:disabled { opacity: .45; cursor: default; }
.btn-cerrar-modal {
  padding: 7px 16px; border-radius: 8px;
  border: 1px solid var(--q-border, #2a2f3d);
  background: transparent; color: var(--q-text, #e8eaf0); font-size: 13px; cursor: pointer;
}
.btn-cerrar-modal:hover { background: rgba(255,255,255,.05); }

/* Invite link */
.det-invite     { display: flex; flex-direction: column; gap: 4px; }
.det-invite-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.det-link {
  font-size: 12px; color: #42a5f5; word-break: break-all;
  text-decoration: none;
}
.det-link:hover { text-decoration: underline; }
.det-link--revocado { color: var(--q-muted, #8891aa); text-decoration: line-through; }
.det-link--nuevo    { color: #66bb6a; font-weight: 600; }
.det-usado-info     { font-size: 11px; opacity: .65; }

.badge-usado    { font-size: 10px; padding: 1px 7px; border-radius: 10px; background: rgba(255,193,7,.15); color: #ffc107; font-weight: 700; }
.badge-sinusar  { font-size: 10px; padding: 1px 7px; border-radius: 10px; background: rgba(102,187,106,.15); color: #66bb6a; font-weight: 700; }
.badge-revocado { font-size: 10px; padding: 1px 7px; border-radius: 10px; background: rgba(239,83,80,.15); color: #ef5350; font-weight: 700; margin-left: 6px; }
.badge-nuevo    { font-size: 10px; padding: 1px 7px; border-radius: 10px; background: rgba(102,187,106,.15); color: #66bb6a; font-weight: 700; margin-left: 6px; }

.det-regen-msg  { font-size: 13px; padding: 8px 12px; border-radius: 8px; }
.det-regen-ok   { background: rgba(102,187,106,.1); color: #66bb6a; }
.det-regen-err  { background: rgba(239,83,80,.1); color: #ef5350; }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .pedidos-view { padding: 12px 12px 60px; }
  .pv-filtros-row { flex-direction: column; align-items: stretch; }
  .pv-search-wrap { min-width: 100%; }
  .pv-select { width: 100%; }
  .btn-periodo { font-size: .75rem; padding: 4px 10px; }
  .date-input { max-width: calc(50vw - 40px); }
  .det-grid { grid-template-columns: 1fr 1fr; }
}
</style>
